import React, { useMemo } from "react";
import { Board as BoardData, BoardSide } from "../engine/types";
import { isDouble } from "../engine/dominoMap";
import { Tile } from "./Tile";
import { useElementSize } from "./responsive";

export interface BoardProps {
  board: BoardData | null;
  pendingCard?: number | null;
  pendingSides?: BoardSide[];
  onPickSide?: (side: BoardSide) => void;
  message?: string | null;
}

interface ChainEntry {
  card: number;
  units: number;
}

function unitsFor(card: number): number {
  return isDouble(card) ? 0.5 : 1;
}

function buildChain(b: BoardData): ChainEntry[] {
  if (!b.cardPosed) return [];
  const left = [...b.leftBoard].reverse().map((c) => ({ card: c, units: unitsFor(c) }));
  const center: ChainEntry = { card: b.posedCard, units: unitsFor(b.posedCard) };
  const right = b.rightBoard.map((c) => ({ card: c, units: unitsFor(c) }));
  return [...left, center, ...right];
}

export const Board: React.FC<BoardProps> = ({
  board,
  pendingCard,
  pendingSides,
  onPickSide,
  message,
}) => {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const chain = useMemo(() => (board ? buildChain(board) : []), [board]);
  const totalUnits = chain.reduce((acc, e) => acc + e.units, 0) || 1;

  const padding = 16;
  const targetRowHeightFraction = 0.35;
  const maxTileSize = 110;
  const minTileSize = 26;

  const containerW = Math.max(0, size.w - padding * 2);
  const containerH = Math.max(0, size.h - padding * 2);

  const widthBudget = containerW / totalUnits;
  const heightBudget = containerH * targetRowHeightFraction * 2;
  let tileSize = Math.min(widthBudget, heightBudget, maxTileSize);
  let rows = 1;
  if (tileSize < minTileSize) {
    rows = Math.ceil(totalUnits / Math.max(1, Math.floor(containerW / minTileSize)));
    rows = Math.min(rows, 4);
    const perRowUnits = totalUnits / rows;
    tileSize = Math.min(containerW / perRowUnits, containerH / (rows * 0.6), maxTileSize);
    tileSize = Math.max(tileSize, 18);
  }

  const showSidePrompt = !!pendingCard && pendingSides && pendingSides.length > 1;

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        padding,
        background:
          "radial-gradient(ellipse at center, #1c5e2d 0%, #0d3a1b 70%, #07260f 100%)",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: padding,
          display: "flex",
          flexWrap: "wrap",
          alignContent: "center",
          justifyContent: "center",
          alignItems: "center",
          gap: Math.max(2, tileSize * 0.04),
          rowGap: Math.max(4, tileSize * 0.15),
        }}
      >
        {chain.length === 0 && (
          <div style={{ color: "#cfead4", fontSize: 14, opacity: 0.7 }}>
            Waiting for the round to begin…
          </div>
        )}
        {chain.map((e, i) => (
          <Tile
            key={`${i}-${e.card}`}
            card={e.card}
            size={tileSize}
            orientation={isDouble(e.card) ? "vertical" : "horizontal"}
          />
        ))}
      </div>

      {showSidePrompt && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 12,
            display: "flex",
            justifyContent: "center",
            gap: 12,
            pointerEvents: "auto",
          }}
        >
          {pendingSides!.map((side) => (
            <button
              key={side}
              onClick={() => onPickSide?.(side)}
              style={{
                background: "#FED100",
                color: "#0d3a1b",
                border: "none",
                fontWeight: 700,
                padding: "10px 18px",
                borderRadius: 999,
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
              }}
            >
              {side === "left" ? "Play left" : side === "right" ? "Play right" : "Pose"}
            </button>
          ))}
        </div>
      )}

      {message && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: 999,
              fontSize: 13,
            }}
          >
            {message}
          </div>
        </div>
      )}
    </div>
  );
};
